import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { fadeInUp, smoothTransition, cardAnimation } from './animations';

const faqs = [
  {
    id: 'faq-technical',
    question: '¿Necesito conocimientos técnicos?',
    answer:
      'No necesitas conocimientos técnicos. Nosotros nos encargamos de toda la parte técnica por ti. No tienes que instalar nada ni aprender herramientas complicadas. Recibes tu menú listo para usarse, solo escaneas el QR y funciona.',
  },
  {
    id: 'faq-business',
    question: '¿Funciona para mi tipo de negocio?',
    answer:
      'Funciona para restaurantes, cafeterías, bares o cualquier negocio con menú. No importa si tienes 10 productos o 100, el sistema se adapta a tu oferta. Lo importante es cómo presentas lo que vendes.',
  },
  {
    id: 'faq-time',
    question: '¿Cuánto tarda la implementación?',
    answer:
      'El proceso es rápido. En pocos días puedes tener tu menú activo y funcionando desde que tenemos tu información.',
  },
  {
    id: 'faq-changes',
    question: '¿Puedo hacer cambios después?',
    answer:
      'Puedes hacer cambios después. Incluye actualizaciones para ajustar precios, agregar productos o hacer mejoras sin complicaciones. Tu menú evoluciona contigo.',
  },
  {
    id: 'faq-duration',
    question: '¿Cuánto tiempo dura el acceso?',
    answer:
      'El acceso incluye 12 meses completos. Después puedes renovarlo para mantener tu menú activo sin interrupciones. Es simple y sin cargos ocultos.',
  },
  {
    id: 'faq-operations',
    question: '¿Tengo que cambiar mi operación?',
    answer:
      'No necesitas cambiar tu operación. Este sistema se integra a tu negocio tal como está. Solo reemplazas tu menú actual por uno que vende mejor.',
  },
  {
    id: 'faq-sales',
    question: '¿Realmente aumentará mis ventas?',
    answer:
      'El objetivo es guiar la decisión del cliente para que pida más y más rápido. No depende de suerte, depende de cómo presentas tus productos.',
  },
  {
    id: 'faq-photos',
    question: '¿Qué pasa si no tengo fotos?',
    answer:
      'Si no tienes fotos, podemos trabajar con tu contenido actual o ayudarte a mejorar la presentación visual.',
  },
];

export const FAQ = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section ref={ref} className="py-20 sm:py-28 relative">
      <div className="container px-4 sm:px-6 lg:px-8">
        <motion.div
          {...fadeInUp}
          animate={inView ? fadeInUp.animate : fadeInUp.initial}
          transition={smoothTransition}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Preguntas frecuentes
          </h2>
          <p className="text-lg text-muted-foreground">
            Sabemos que antes de tomar una decisión puedes tener algunas dudas, aquí te respondemos las más comunes.
          </p>
        </motion.div>

        <motion.div
          {...cardAnimation(0.2)}
          animate={inView ? cardAnimation(0.2).animate : cardAnimation(0.2).initial}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="glass-card border border-border/50 rounded-xl px-6 data-[state=open]:border-primary/30 transition-all duration-300"
              >
                <AccordionTrigger className="text-left hover:no-underline py-5">
                  <span className="text-base sm:text-lg font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
